const fetchConfig: RequestInit = {
  headers: {
    "Content-Type": "application/json"
  }
};

async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getJWTUserToken();
  const headers = {
    ...fetchConfig.headers,
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
  return fetch(url, { ...options, headers });
}

async function logout(url: string): Promise<any> {
  const token = getJWTUserToken();
  deleteJWTUser();
  if (token) {
    const response = await fetch(url, {
      method: "POST",
      ...fetchConfig,
      headers: {
        ...fetchConfig.headers,
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || response.statusText);
    }
    return response.json();
  }
  return Promise.resolve();
}

async function login(
  url: string,
  email: string,
  password: string,
  recaptcha: string
): Promise<User> {
  const response = await fetch(url, {
    method: "POST",
    ...fetchConfig,
    body: JSON.stringify({ email, password, recaptcha })
  });

  if (!response.ok) {
    await logout(url);

    let errorMessage = response.statusText;
    try {
      const errorText = await response.text();
      if (errorText && errorText.trim()) {
        errorMessage = errorText;
      }
    } catch (_) {}

    throw new Error(errorMessage || "Login failed");
  }

  const data: AuthResponse = await response.json();
  if (data.user && data.token) {
    setJWTUser({ ...data.user, token: data.token });
    return data.user;
  }
  throw new Error("Login failed");
}

async function activate(url: string, code: string): Promise<User> {
  const response = await fetch(url, {
    method: "POST",
    ...fetchConfig,
    body: JSON.stringify({ code })
  });

  if (!response.ok) {
    await logout(url);
    const error = await response.text();
    throw new Error(error || response.statusText);
  }

  const data: AuthResponse = await response.json();
  if (data.user && data.token) {
    setJWTUser({ ...data.user, token: data.token });
    return data.user;
  }
  throw new Error("Activation failed");
}

async function signup(
  url: string,
  name: string,
  surname: string,
  email: string,
  password: string,
  passwordConfirmation: string,
  countryId: number,
  countryStateId: number,
  teamLead: boolean,
  termsAcceptance: boolean,
  company: string,
  job: string
): Promise<User> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      ...fetchConfig,
      body: JSON.stringify({
        name,
        surname,
        email,
        password,
        password_confirmation: passwordConfirmation,
        country_id: countryId,
        state_id: countryStateId,
        is_product_leader: teamLead,
        terms_of_use_accepted: termsAcceptance,
        privacy_policy_accepted: termsAcceptance,
        company,
        job_title: job
      })
    });
  } catch (error) {
    // Network-level failure (fetch rejected) — no response to parse.
    throw new Error(
      JSON.stringify({
        error: true,
        message: "Signup failed due to an unexpected error"
      })
    );
  }

  if (!response.ok) {
    const text = await response.text();

    // The API returns { error: true, message: "..." }. Extract the real
    // message so it can be surfaced to the user, falling back to raw text.
    let message = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.message) {
        message = parsed.message;
      }
    } catch {
      // Response body was not JSON — keep the raw text as the message.
    }

    throw new Error(JSON.stringify({ error: true, message }));
  }

  const data: any = await response.json();
  if (data.user) {
    setJWTUser({ ...data.user, token: data.token });
    return data.user;
  }
  return data;
}

async function update(
  url: string,
  name: string,
  surname: string,
  email: string,
  countryId: number,
  countryStateId: number,
  teamLead: number,
  loginUsername: string,
  loginPassword: string,
  password: string,
  passwordConfirmation: string
): Promise<any> {
  const token = getJWTUserToken();
  const payload: any = {
    name,
    surname,
    email,
    current_login: loginUsername,
    current_password: loginPassword,
    country_id: countryId,
    state_id: countryStateId,
    is_product_leader: teamLead
  };

  if (
    password &&
    passwordConfirmation &&
    password.trim() !== "" &&
    passwordConfirmation.trim() !== "" &&
    password === passwordConfirmation
  ) {
    payload.password = password;
    payload.password_confirmation = passwordConfirmation;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...fetchConfig.headers,
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const errorText = await response.text();
    useNotificationStore().error({
      variant: "error",
      messageHead: "Update failed:",
      messageBody: errorText || response.statusText
    });
    throw new Error(errorText || response.statusText);
  }

  const json = await response.json();
  return json;
}

async function getCountries(url: string): Promise<any> {
  const response = await fetch(url, fetchConfig);

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || response.statusText);
  }

  return response.json();
}

async function forgotpassword(
  url: string,
  email: string,
  recaptcha: string
): Promise<void> {
  const formdata = new FormData();
  formdata.append("email", email);
  formdata.append("recaptcha", recaptcha);

  const response = await fetch(url, {
    method: "POST",
    body: formdata
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || response.statusText);
  }
}

async function resetpassword(
  url: string,
  code: string,
  password: string,
  passwordConfirmation: string,
  recaptcha: string
): Promise<void> {
  const formdata = new FormData();
  formdata.append("code", code);
  formdata.append("password", password);
  formdata.append("password_confirmation", passwordConfirmation);
  formdata.append("recaptcha", recaptcha);

  const response = await fetch(url, {
    method: "POST",
    body: formdata
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || response.statusText);
  }
}

async function checkToken(url: string): Promise<any> {
  const user = getJWTUser() || null;
  const token = getJWTUserToken();

  if (!token) {
    return Promise.resolve();
  }

  const response = await fetch(url, {
    headers: {
      ...fetchConfig.headers,
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || response.statusText);
  }

  const data = await response.json();
  if (data.user_id !== user?.id) {
    throw new Error("User IDs don't match");
  }

  return data;
}

async function refreshToken(url: string): Promise<string> {
  const token = getJWTUserToken();

  if (!token) {
    throw new Error(
      "Failed to refresh authentication token as original token missing"
    );
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      ...fetchConfig.headers,
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || response.statusText);
  }

  const data = await response.json();
  const user = getJWTUser() || null;
  const newToken = data.token || null;

  if (user && newToken) {
    user.token = newToken;
    setJWTUser(user);
    return newToken;
  }

  throw new Error("Failed to refresh authentication token");
}

export default {
  login,
  logout,
  signup,
  activate,
  update,
  forgotpassword,
  resetpassword,
  checkToken,
  refreshToken,
  getCountries
};
